// File Name: pages/terms-and-policy-pages/AppPrivacyPolicy.js
//
// Privacy Policy for the YNO mobile app. Route: /app/privacy
//
// ⚠️ This describes what the app ACTUALLY does — it was written against the
// source, not from a template. If the app's data handling changes, this changes
// with it. In particular these claims are load-bearing and were verified:
//
//   * contacts are matched in memory and never stored (services/contact_matcher.dart)
//   * no location data is collected (no location package in pubspec.yaml)
//   * no advertising or analytics SDK is present
//   * match records are world-readable, which is what makes the public web
//     scoreboard work (firestore.rules)
//   * in-app deletion exists (Settings → Delete my account, email code, the
//     Worker's /delete route); email to support is the fallback
//   * app opens are recorded per Dubai day (services/daily_activity.dart) and
//     drive the daily bonus and the 7pm reminder (server/src/reminders.js)
//   * bug reports / suggestions carry name, email, app version, platform
//     (services/feedback_repository.dart)
//   * the Play install referrer is read once, on first launch, for referral
//     attribution (services/referral_link_service.dart)

import React from "react";
import { Typography } from "antd";
import { Link } from "react-router-dom";
import LegalPageShell, { APP_LEGAL_CONTACT } from "./LegalPageShell";

const { Title, Paragraph, Text } = Typography;

const AppPrivacyPolicy = () => (
  <LegalPageShell
    title="YNO App — Privacy Policy"
    subtitle="How the YNO mobile application collects, uses and protects your information."
  >
    <Title level={4}>1. Who We Are</Title>
    <Paragraph>
      Yalla Nellab ("we", "us", "our") is a company incorporated in the United
      Arab Emirates. We provide the YNO mobile application (the "App"), which
      lets players organise football matches, form teams, record match results
      and track their playing statistics.
    </Paragraph>
    <Paragraph>
      This Privacy Policy applies to the App only. Our website and our in-person
      events are covered by our separate website Privacy Policy.
    </Paragraph>

    <Title level={4}>2. Information You Give Us</Title>
    <Paragraph>When you create an account or edit your profile, we collect:</Paragraph>
    <ul>
      <li>Your first and last name, and the display name shown to other players</li>
      <li>Your email address</li>
      <li>Your phone number (optional)</li>
      <li>Your date of birth and gender (optional)</li>
      <li>Your profile photo, if you choose to upload one</li>
      <li>
        Football profile details: playing position, preferred foot and
        self-assessed skill level
      </li>
      <li>Links to your social media profiles, if you choose to add them</li>
      <li>Your language preference</li>
    </ul>
    <Paragraph>
      If you sign in with Google, we receive your name, email address and profile
      picture from Google. We never receive your Google password.
    </Paragraph>

    <Title level={4}>3. Information Created by Using the App</Title>
    <ul>
      <li>
        Matches you create or join, the teams involved, the format and duration,
        and the match's join code
      </li>
      <li>
        Match results and events: scores, goals, assists, the minute each goal
        was scored, man-of-the-match votes and the outcome of a match
      </li>
      <li>
        Career statistics derived from those results: matches played, wins,
        losses, draws, goals, assists, streaks and similar figures
      </li>
      <li>Teams you own, captain or belong to, and invitations you send or receive</li>
      <li>Friends, follows, and friend requests</li>
      <li>
        Your referral code, who referred you, and the points you have earned,
        including points for opening the App daily, inviting friends, adding
        friends, creating teams, joining or creating matches, and sending
        feedback
      </li>
      <li>Notifications sent to you within the App</li>
      <li>
        Bug reports and suggestions you choose to send us through the App
        (see section 7)
      </li>
    </ul>

    <Title level={4}>4. Information Collected Automatically</Title>
    <ul>
      <li>
        A push notification token for your device, so we can deliver
        notifications. It is removed if you uninstall the App or sign out.
      </li>
      <li>The App version, used for support and compatibility.</li>
      <li>
        <Text strong>App activity.</Text> Each day you open the App we record
        that day, the time of your most recent open, and your current and best
        run of consecutive days. We keep the list of days for the last 60 days.
        This powers the daily bonus, your streak, and the daily reminder
        described in section 18. We do not record what you do inside the App
        beyond the actions listed in section 3.
      </li>
      <li>
        <Text strong>Install referrer (Android only).</Text> On first launch we
        read the Google Play install referrer once, to see whether you installed
        the App from a friend&apos;s invitation link. If it contains a YNO
        referral code, that code is stored on your account so the friend
        receives their referral points. Nothing else from it is kept, and it is
        never read again.
      </li>
    </ul>
    <Paragraph>
      <Text strong>We do not collect your location.</Text> The App contains no
      location-tracking functionality. Any venue or pitch name attached to a match
      is free text typed by the person who created it.
    </Paragraph>
    <Paragraph>
      <Text strong>
        We do not use advertising networks, and we do not run third-party
        analytics or tracking SDKs in the App.
      </Text>
    </Paragraph>

    <Title level={4}>5. Contacts</Title>
    <Paragraph>
      The App offers an optional feature that finds friends already using YNO by
      checking your phone's contacts. It is <Text strong>off by default</Text> and
      runs only if you turn it on and grant the contacts permission.
    </Paragraph>
    <Paragraph>How it works, precisely:</Paragraph>
    <ul>
      <li>
        Phone numbers from your address book are read on your device and put into
        a standard format in the device's memory.
      </li>
      <li>
        Those numbers are used to run a single lookup against our player database
        to see which of them belong to existing YNO accounts.
      </li>
      <li>
        <Text strong>
          Your contacts are not stored on our servers, are not added to any
          profile, and are not shared with anyone.
        </Text>{" "}
        They are discarded as soon as the lookup finishes. We only keep the
        result, which is a list of existing YNO players you may already know.
      </li>
      <li>
        Names, email addresses and other details of your contacts are not used.
      </li>
    </ul>
    <Paragraph>
      You can withdraw the contacts permission at any time in your device
      settings, and turn the feature off inside the App.
    </Paragraph>

    <Title level={4}>6. Photographs</Title>
    <Paragraph>
      If you upload a profile photo or a team badge, the image is stored with our
      image hosting provider (Cloudinary) and served over the internet by them.
      When you replace a profile photo, the previous image is deleted. The App
      requests access to your camera or photo library only at the moment you
      choose to upload an image.
    </Paragraph>

    <Title level={4}>7. Bug Reports and Suggestions</Title>
    <Paragraph>
      You can send us a bug report or a suggestion from inside the App. Together
      with your message we store your name, your email address, the App version
      and your device type (Android or iOS), so that we can reproduce the problem
      and reply to you. The App tells you this before you send. Our team reads
      these reports, may reply by email, and may award points for helpful ones.
      Reports are kept while they are being worked on and for a reasonable period
      afterwards.
    </Paragraph>

    <Title level={4}>8. Guest Players</Title>
    <Paragraph>
      A match host can add someone to a match who does not have an account (a
      "guest"). For a guest we store the name entered, and optionally a phone
      number or email address, together with the matches they played and the
      statistics recorded for them.
    </Paragraph>
    <Paragraph>
      If you have been added as a guest and would like that record removed, or
      would like to claim it as your own account, contact us at{" "}
      <Text strong>{APP_LEGAL_CONTACT}</Text>.
    </Paragraph>

    <Title level={4}>9. Accounts Created For You</Title>
    <Paragraph>
      A match host may create an account on your behalf using your email address
      so you can be added to a match. If this happens, you can claim the account
      through the "Claim my account" option in the App: we email a one-time
      verification code to that address, and you can then set your own password.
      Verification codes are stored only in an encrypted (hashed) form, expire
      after a short period, and can be used once.
    </Paragraph>
    <Paragraph>
      <Text strong>
        We recommend claiming your account and setting your own password as soon
        as you can.
      </Text>
    </Paragraph>

    <Title level={4}>10. What Other People Can See</Title>
    <Paragraph>
      YNO is a social app, and some information is visible to others by design:
    </Paragraph>
    <ul>
      <li>
        Your display name, profile photo, football profile and career statistics
        are visible to other players, including on leaderboards and match
        scorecards. You can make your profile private in Settings.
      </li>
      <li>
        Players in a match can see the other participants, the line-ups and the
        result.
      </li>
      <li>
        <Text strong>
          Match records, including team names, scores and the names of goal
          scorers, can be viewed publicly on our website by anyone holding the
          match's link or code, without signing in.
        </Text>{" "}
        This is what allows friends and family to follow a match live. Do not put
        information in a team name, match name or profile that you would not want
        to be public.
      </li>
    </ul>

    <Title level={4}>11. How We Use Your Information</Title>
    <ul>
      <li>To create and operate your account and authenticate you</li>
      <li>To run matches, teams, invitations and join codes</li>
      <li>
        To calculate and display statistics, points and leaderboards, and to
        award points for activity such as opening the App daily
      </li>
      <li>
        To send you notifications about matches, invitations and friend activity,
        according to your notification settings, and a daily reminder if you
        have not opened the App that day
      </li>
      <li>To read and act on the bug reports and suggestions you send us</li>
      <li>To provide support and respond to your requests</li>
      <li>
        To keep the service secure, prevent abuse, and enforce our Terms &amp;
        Conditions
      </li>
      <li>To comply with legal obligations</li>
    </ul>
    <Paragraph>
      We rely on your consent (for optional features such as contacts matching and
      notifications), on the performance of our contract with you (to operate the
      account and the service you asked for), on our legitimate interests (to keep
      the service secure and to improve it), and on legal obligations where they
      apply.
    </Paragraph>

    <Title level={4}>12. Who We Share It With</Title>
    <Paragraph>
      <Text strong>We do not sell your personal data, and we do not share it for
      advertising.</Text>{" "}
      We use the following service providers, who process data on our behalf:
    </Paragraph>
    <ul>
      <li>
        <Text strong>Google Firebase</Text> — account sign-in, database hosting
        and push notification delivery
      </li>
      <li>
        <Text strong>Cloudinary</Text> — hosting of profile photos and team badges
      </li>
      <li>
        <Text strong>Cloudflare</Text> — hosting of our account service, which
        issues verification codes, carries out account deletion, and sends the
        daily reminder notification
      </li>
      <li>
        <Text strong>Resend</Text> — delivery of verification and service emails
      </li>
    </ul>
    <Paragraph>
      We may also disclose information where we are required to do so by law, or
      to protect our rights, safety, or the safety of others.
    </Paragraph>

    <Title level={4}>13. International Transfers</Title>
    <Paragraph>
      Our service providers operate internationally, so your information may be
      stored or processed outside the United Arab Emirates. Where that happens, we
      take reasonable steps to ensure your information continues to receive an
      appropriate level of protection.
    </Paragraph>

    <Title level={4}>14. How Long We Keep It</Title>
    <ul>
      <li>
        Profile and account data: for as long as your account exists, and for a
        short period afterwards.
      </li>
      <li>
        Match records and statistics: retained as part of the match history of
        every participant. If you delete your account, your personal details are
        removed, but a match may still show that it took place and its result.
      </li>
      <li>Verification codes: minutes, then deleted.</li>
      <li>App-open history: the last 60 days on a rolling basis.</li>
      <li>
        Bug reports and suggestions: while they are being handled and for a
        reasonable period afterwards, or until you delete your account.
      </li>
      <li>Unstarted draft matches: automatically deleted after two days.</li>
    </ul>

    <Title level={4}>15. Deactivating or Deleting Your Account</Title>
    <Paragraph>
      <Text strong>Deactivation.</Text> In Settings you can temporarily deactivate
      your account for a period you choose, up to one month. While deactivated,
      your profile is hidden from discovery. It reactivates when you sign back in
      after that period.
    </Paragraph>
    <Paragraph>
      <Text strong>Deletion.</Text> You can permanently delete your account from
      inside the App: open <Text strong>Settings</Text>, tap{" "}
      <Text strong>Delete my account</Text>, and confirm with the 6-digit code we
      email to the address on your account. Deletion is immediate and cannot be
      undone. If you no longer have the App, email{" "}
      <Text strong>{APP_LEGAL_CONTACT}</Text> from the address on your account and
      we will action the request without undue delay, and in any case within 30
      days. Full details of what is deleted and what is kept are on our{" "}
      <Link to="/app/delete-account">Delete Your Account</Link> page.
    </Paragraph>

    <Title level={4}>16. Your Rights</Title>
    <Paragraph>
      Depending on where you live, you may have the right to access your personal
      data, to correct it, to delete it, to object to or restrict how we use it,
      to withdraw consent, and to receive a copy in a portable format. Much of
      this you can do yourself inside the App by editing your profile and
      settings. For anything else, contact{" "}
      <Text strong>{APP_LEGAL_CONTACT}</Text>. You also have the right to complain
      to your local data protection authority.
    </Paragraph>

    <Title level={4}>17. Children</Title>
    <Paragraph>
      The App is not intended for children under 13, and we do not knowingly
      create accounts for them. If you are under 18, you may use the App only with
      the consent and supervision of a parent or guardian, who must accept our
      Terms &amp; Conditions on your behalf.
    </Paragraph>
    <Paragraph>
      If you believe a child has provided us with personal data without the
      appropriate consent, contact us at{" "}
      <Text strong>{APP_LEGAL_CONTACT}</Text> and we will remove it.
    </Paragraph>

    <Title level={4}>18. Notifications</Title>
    <Paragraph>
      We send push notifications about matches, invitations and friend activity.
      You can control which of these you receive in the App&apos;s Settings. We
      may also send one reminder in the evening (around 7pm Gulf Standard Time)
      on days you have not opened the App, in your chosen language. You can turn
      off notifications entirely in your device settings, which also stops the
      reminder.
    </Paragraph>

    <Title level={4}>19. Security</Title>
    <Paragraph>
      We use industry-standard measures to protect your information, including
      encrypted connections and access controls, and verification codes are stored
      only in hashed form. No method of transmission or storage is completely
      secure, and we cannot guarantee absolute security. Keep your password
      confidential and do not share your account.
    </Paragraph>

    <Title level={4}>20. Changes to This Policy</Title>
    <Paragraph>
      We may update this Privacy Policy from time to time. When we do, we will
      change the Effective Date above, and where the change is significant we will
      tell you in the App.
    </Paragraph>

    <Title level={4}>21. Contact Us</Title>
    <Paragraph>
      Questions about this Privacy Policy or about your data can be sent to{" "}
      <Text strong>{APP_LEGAL_CONTACT}</Text>.
    </Paragraph>
  </LegalPageShell>
);

export default AppPrivacyPolicy;
